import { useCallback, useEffect, useState } from 'react';
import { type Conversation } from '@jabberr/core';
import { api } from '../../lib/api.js';

/** Recent threads per project, shown (expandable) under each project row. */
export type ThreadRow = Conversation & { lastMentionAt?: string | null; unreadMention?: boolean };

export interface ProjectThreads {
  threads: Record<string, ThreadRow[]>;
  threadsComplete: Record<string, boolean>;
  expanded: Set<string>;
  loadThreads: (pid: string, all?: boolean) => void;
  toggleProject: (pid: string) => void;
  ensureExpanded: (pid: string) => void;
  prependThread: (pid: string, conversation: Conversation) => void;
  updateThread: (pid: string, conversation: Conversation) => void;
  removeThread: (pid: string, conversationId: string) => void;
  renameThread: (threadId: string, title: string) => Promise<Conversation | null>;
  archiveThread: (threadId: string) => Promise<Conversation | null>;
}

/** Mention badge (OPEN-23): read state lives on the server now; the conversation list carries unreadMention. */
export function isUnmentioned(c: ThreadRow, openConversationId: string | undefined): boolean {
  return !!c.unreadMention && c.id !== openConversationId;
}

export function useProjectThreads(options: {
  projectId: string | undefined;
  openConversationId: string | undefined;
  pathname: string;
  drawerInfo: unknown;
}): ProjectThreads {
  const { projectId, openConversationId, pathname, drawerInfo } = options;
  const [threads, setThreads] = useState<Record<string, ThreadRow[]>>({});
  const [threadsComplete, setThreadsComplete] = useState<Record<string, boolean>>({});
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  const loadThreads = useCallback((pid: string, all = false) => {
    api.conversations
      .list(pid)
      .then(({ conversations }) => {
        setThreads((prev) => ({ ...prev, [pid]: all ? conversations : conversations.slice(0, 8) }));
        setThreadsComplete((prev) => ({ ...prev, [pid]: all }));
      })
      .catch(() => undefined);
  }, []);

  // The current project auto-expands and its thread list stays fresh (navigation and drawer closes re-read it).
  useEffect(() => {
    if (!projectId) return;
    setExpanded((prev) => (prev.has(projectId) ? prev : new Set(prev).add(projectId)));
    loadThreads(projectId);
  }, [projectId, pathname, drawerInfo, loadThreads]);

  const toggleProject = useCallback(
    (pid: string) => {
      setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(pid)) next.delete(pid);
        else {
          next.add(pid);
          loadThreads(pid);
        }
        return next;
      });
    },
    [loadThreads],
  );

  const ensureExpanded = useCallback((pid: string) => {
    setExpanded((prev) => (prev.has(pid) ? prev : new Set(prev).add(pid)));
  }, []);

  const prependThread = useCallback((pid: string, conversation: Conversation) => {
    setThreads((prev) => ({ ...prev, [pid]: [conversation, ...(prev[pid] ?? [])].slice(0, 8) }));
  }, []);

  const updateThread = useCallback((pid: string, conversation: Conversation) => {
    setThreads((prev) => ({ ...prev, [pid]: (prev[pid] ?? []).map((c) => (c.id === conversation.id ? conversation : c)) }));
  }, []);

  const removeThread = useCallback((pid: string, conversationId: string) => {
    setThreads((prev) => ({ ...prev, [pid]: (prev[pid] ?? []).filter((c) => c.id !== conversationId) }));
  }, []);

  const renameThread = useCallback(async (threadId: string, title: string): Promise<Conversation | null> => {
    try {
      const { conversation } = await api.conversations.update(threadId, { title });
      return conversation;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      return null;
    }
  }, []);

  const archiveThread = useCallback(async (threadId: string): Promise<Conversation | null> => {
    try {
      const { conversation } = await api.conversations.update(threadId, { status: 'archived' });
      return conversation;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      return null;
    }
  }, []);

  return {
    threads,
    threadsComplete,
    expanded,
    loadThreads,
    toggleProject,
    ensureExpanded,
    prependThread,
    updateThread,
    removeThread,
    renameThread,
    archiveThread,
  };
}
